import {
  BalanceColumn,
  DonutChartOptions,
  ModalDetailChartOptions,
  PageBalance,
  RadialBarChartOptions,
  ReferralChartOptions,
} from '../../../core/interfaces/page-balance.interface';
import { BalanceLoadState } from '../../../core/interfaces/ui/balance-load-data.interface';

const fmt = (v: number) =>
  v.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const $c = (v: number) => `$${fmt(v)}`;

const pn = (v: number) => (v > 0 ? 'text-success' : v < 0 ? 'text-danger' : '');

const fd = (date: any): string => {
  const d = new Date(date);
  const dd = d.getDate().toString().padStart(2, '0');
  const mm = (d.getMonth() + 1).toString().padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
};

const inGanRef = (i: PageBalance): number =>
  (i.incomingRef1 ?? 0) + (i.incomingRef2 ?? 0) + (i.incomingRef3 ?? 0);

const ganEmp = (i: PageBalance): number => {
  if (i.actualBalance < 0) return i.actualBalance;
  if (i.balance < 0) return i.actualBalance;
  if (i.lastBalance < 0) return i.actualBalance - Number(i.result);
  return (i.actualBalance * (100 - i.percent)) / 100;
};

const totEmp = (i: PageBalance): number => ganEmp(i) - inGanRef(i);

const pctNeto = (i: PageBalance): number => {
  const t = totEmp(i);
  if (i.balance > 0) return (t / i.balance) * 100;
  if (i.balance === 0) return 0;
  return -(t / i.balance) * 100;
};

export const PAGE_BALANCE_COLUMNS: BalanceColumn[] = [
  { key: 'actions', header: 'Acción', type: 'actions' },
  {
    key: 'date',
    header: 'Fecha',
    type: 'cell',
    format: (row) => fd(row.date),
    sortValue: (row) => new Date(row.date).getTime(),
  },
  {
    key: 'agent',
    header: 'Agente',
    type: 'cell',
    format: (row) => row.agent?.usuario ?? '',
    sortValue: (row) => row.agent?.usuario ?? '',
  },
  {
    key: 'detail',
    header: 'Detalle',
    type: 'cell',
    format: (row) => row.detail,
    sortValue: (row) => row.detail || '',
  },
  {
    key: 'lastBalance',
    header: 'Balance anterior',
    type: 'cell',
    format: (row) => $c(row.lastBalance),
    sortValue: (row) => row.lastBalance,
  },
  {
    key: 'actualBalance',
    header: 'Balance actual',
    type: 'cell',
    format: (row) => $c(row.actualBalance),
    sortValue: (row) => row.actualBalance,
  },
  {
    key: 'balance',
    header: 'Balance',
    type: 'cell',
    bold: true,
    format: (row) => $c(row.balance),
    cellClass: (row) => pn(row.balance),
    sortValue: (row) => row.balance,
  },
  {
    key: 'percent',
    header: 'Porcentaje',
    type: 'cell',
    bold: true,
    format: (row) => `${row.percent}%`,
    sortValue: (row) => row.percent,
  },
  {
    key: 'result',
    header: 'Valor final',
    type: 'cell',
    bold: true,
    format: (row) => $c(+row.result),
    cellClass: () => 'text-info',
    sortValue: (row) => +row.result,
  },
  {
    key: 'drag',
    header: 'Arrastre',
    type: 'badge',
    format: (row) =>
      row.drag === 0 ? 'Sin arrastre' : row.drag === 1 ? 'Con arrastre' : 'Arrastre V2',
    badgeClass: (row) =>
      row.drag === 0 ? 'bg-danger' : row.drag === 1 ? 'bg-success' : 'bg-warning',
    sortValue: (row) => row.drag,
  },
  {
    key: 'lastBet',
    header: 'Apuestas anterior',
    type: 'cell',
    format: (row) => $c(+row.lastBet),
    sortValue: (row) => +row.lastBet,
  },
  {
    key: 'bet',
    header: 'Apuestas actual',
    type: 'cell',
    format: (row) => $c(row.bet),
    sortValue: (row) => row.bet,
  },
  {
    key: 'ggrLastBalance',
    header: 'Casino anterior',
    type: 'cell',
    format: (row) => $c(+row.ggrLastBalance),
    cellClass: (row) => pn(+row.ggrLastBalance),
    sortValue: (row) => +row.ggrLastBalance,
  },
  {
    key: 'ggrActualBalance',
    header: 'Casino actual',
    type: 'cell',
    format: (row) => $c(+row.ggrActualBalance),
    cellClass: (row) => pn(+row.ggrActualBalance),
    sortValue: (row) => +row.ggrActualBalance,
  },
  {
    key: 'ggrCasinoBalance',
    header: 'Casino',
    type: 'cell',
    format: (row) => $c(+row.ggrCasinoBalance),
    cellClass: (row) => pn(+row.ggrCasinoBalance),
    sortValue: (row) => +row.ggrCasinoBalance,
  },
  {
    key: 'ggrResult',
    header: 'GGR Resultado',
    type: 'cell',
    bold: true,
    format: (row) => $c(+row.ggrResult),
    cellClass: () => 'text-info',
    sortValue: (row) => +row.ggrResult,
  },
  {
    key: 'betResult',
    header: 'Res. apuesta',
    type: 'cell',
    format: (row) => $c(+row.betResult),
    cellClass: () => 'text-info',
    sortValue: (row) => +row.betResult,
  },
  {
    key: 'ref1',
    header: 'Nivel 1',
    type: 'cell',
    format: (row) => ((row.incomingRef1 ?? 0) > 0 ? $c(row.incomingRef1 ?? 0) : '—'),
    cellClass: (row) => ((row.incomingRef1 ?? 0) === 0 ? 'f-light' : ''),
    sortValue: (row) => row.incomingRef1 ?? 0,
  },
  {
    key: 'ref2',
    header: 'Nivel 2',
    type: 'cell',
    format: (row) => ((row.incomingRef2 ?? 0) > 0 ? $c(row.incomingRef2 ?? 0) : '—'),
    cellClass: (row) => ((row.incomingRef2 ?? 0) === 0 ? 'f-light' : ''),
    sortValue: (row) => row.incomingRef2 ?? 0,
  },
  {
    key: 'ref3',
    header: 'Nivel 3',
    type: 'cell',
    format: (row) => ((row.incomingRef3 ?? 0) > 0 ? $c(row.incomingRef3 ?? 0) : '—'),
    cellClass: (row) => ((row.incomingRef3 ?? 0) === 0 ? 'f-light' : ''),
    sortValue: (row) => row.incomingRef3 ?? 0,
  },
  {
    key: 'referredTotal',
    header: 'Ganancia referido',
    type: 'cell',
    bold: true,
    format: (row) => $c(inGanRef(row)),
    cellClass: () => 'text-warning',
    sortValue: (row) => inGanRef(row),
  },
  {
    key: 'debtAdjustment',
    header: 'Ajuste deuda',
    type: 'cell',
    format: (row) =>
      (row.debtAdjustmentTotal ?? 0) !== 0 ? $c(row.debtAdjustmentTotal ?? 0) : '—',
    cellClass: (row) =>
      (row.debtAdjustmentTotal ?? 0) < 0
        ? 'text-danger'
        : (row.debtAdjustmentTotal ?? 0) > 0
          ? 'text-success'
          : 'f-light',
    sortValue: (row) => row.debtAdjustmentTotal ?? 0,
  },
  {
    key: 'agentTotal',
    header: 'Gan. total agente',
    type: 'cell',
    bold: true,
    format: (row) => $c(+row.result + inGanRef(row) + (row.debtAdjustmentTotal ?? 0)),
    cellClass: () => 'text-info',
    sortValue: (row) => +row.result + inGanRef(row) + (row.debtAdjustmentTotal ?? 0),
  },
  {
    key: 'percentEmpresa',
    header: '% Empresa',
    type: 'cell',
    bold: true,
    format: (row) => `${100 - row.percent}%`,
    sortValue: (row) => 100 - row.percent,
  },
  {
    key: 'companyProfit',
    header: 'Ganancia empresa',
    type: 'cell',
    bold: true,
    format: (row) => $c(ganEmp(row)),
    cellClass: (row) => pn(ganEmp(row)),
    sortValue: (row) => ganEmp(row),
  },
  {
    key: 'companyTotal',
    header: 'Total gan. empresa',
    type: 'cell',
    bold: true,
    format: (row) => $c(totEmp(row)),
    cellClass: (row) => pn(totEmp(row)),
    sortValue: (row) => totEmp(row),
  },
  {
    key: 'netPercent',
    header: '% Neto ganancia',
    type: 'cell',
    bold: true,
    format: (row) => `${fmt(pctNeto(row))}%`,
    cellClass: (row) => pn(pctNeto(row)),
    sortValue: (row) => pctNeto(row),
  },
];

export const PageBalanceMonths = [
  { label: 'Enero', value: 1 },
  { label: 'Febrero', value: 2 },
  { label: 'Marzo', value: 3 },
  { label: 'Abril', value: 4 },
  { label: 'Mayo', value: 5 },
  { label: 'Junio', value: 6 },
  { label: 'Julio', value: 7 },
  { label: 'Agosto', value: 8 },
  { label: 'Septiembre', value: 9 },
  { label: 'Octubre', value: 10 },
  { label: 'Noviembre', value: 11 },
  { label: 'Diciembre', value: 12 },
];

export const PageBalanceParamOptions = [
  { label: 'Menor o igual (<=)', value: 0 },
  { label: 'Menor (<)', value: 1 },
  { label: 'Igual (=)', value: 2 },
  { label: 'Mayor (>)', value: 3 },
  { label: 'Mayor o igual (>=)', value: 4 },
];

export const PageBalancePercentOptions = [
  { label: '10%', value: 10 },
  { label: '20%', value: 20 },
  { label: '25%', value: 25 },
  { label: '30%', value: 30 },
  { label: '35%', value: 35 },
  { label: '40%', value: 40 },
  { label: '45%', value: 45 },
  { label: '50%', value: 50 },
  { label: '55%', value: 55 },
];

export const PageBalanceDragOptions = [
  { label: 'Sin arrastre', value: 0 },
  { label: 'Con arrastre', value: 1 },
];

export const PageBalanceReferrePercentOptions = [
  { label: '0%', value: 0 },
  { label: '5%', value: 5 },
  { label: '10%', value: 10 },
  { label: '15%', value: 15 },
  { label: '20%', value: 20 },
  { label: '25%', value: 25 },
  { label: '30%', value: 30 },
];

export const DONUT_CHART_LABELS = ['P. Negativos', 'P. Positivos'];

export const DONUT_CHART_COLORS = ['#FC4438', '#54BA4A'];

export const DEFAULT_DONUT_CHART: DonutChartOptions = {
  series: [0, 0],
  chart: {
    type: 'donut',
    height: 450,
  },
  labels: DONUT_CHART_LABELS,
  colors: DONUT_CHART_COLORS,
  fill: {
    opacity: 1,
  },
  stroke: {
    width: 2,
    colors: ['#fff'],
  },
  plotOptions: {
    pie: {
      donut: {
        size: '60%',
        labels: {
          show: true,
          total: {
            show: true,
            label: 'Total',
            formatter: (w) => {
              const negative = w.globals.series[0] || 0;
              const positive = w.globals.series[1] || 0;
              const total = positive - negative;
              const prefix = total < 0 ? '-' : '';
              return prefix + Math.abs(total).toLocaleString('en-US');
            },
          },
        },
      },
    },
  },
  dataLabels: {
    enabled: true,
    formatter(val: number): string {
      return val.toFixed(0) + '%';
    },
  },
  legend: {
    position: 'bottom',
    formatter(legendName: string, opts: any): string {
      const value = opts.w.globals.series[opts.seriesIndex];
      return `${legendName}  = ${value.toLocaleString('en-US')}`;
    },
  },
  responsive: [
    {
      breakpoint: 480,
      options: {
        chart: { height: 250 },
        legend: { position: 'bottom' },
      },
    },
  ],
};

// ── Stat Cards ──

export interface StatCard {
  title: string;
  icon: string;
  value: number;
  count: number;
  formattedValue: string;
  percentChange: number;
  sparklineData: number[];
  colorClass: string;
  rangedData?: { categories: string[]; series: number[]; counts: number[] };
}

export const SPARKLINE_CHART_BASE = {
  chart: {
    type: 'area' as const,
    height: 60,
    sparkline: { enabled: true },
  },
  stroke: { curve: 'smooth' as const, width: 2 },
  fill: {
    type: 'gradient',
    gradient: {
      shadeIntensity: 1,
      opacityFrom: 0.4,
      opacityTo: 0.1,
    },
  },
  tooltip: { enabled: false },
};

export const REFERRAL_CHART_COLORS = ['#FC4438', '#FFAA05', '#7366FF'];

export const REFERRAL_LABELS = [
  { key: 'referred1', label: 'Nivel 1', color: '#FC4438' },
  { key: 'referred2', label: 'Nivel 2', color: '#FFAA05' },
  { key: 'referred3', label: 'Nivel 3', color: '#7366FF' },
];

export const DEFAULT_REFERRAL_CHART: ReferralChartOptions = {
  series: [],
  chart: {
    type: 'line',
    height: 60,
    sparkline: { enabled: true },
  },
  colors: REFERRAL_CHART_COLORS,
  stroke: {
    width: 2,
    curve: 'smooth',
  },
  xaxis: {
    categories: ['Inicio', 'Fin'],
    labels: { show: false },
    axisBorder: { show: false },
    axisTicks: { show: false },
  },
  yaxis: {
    labels: { show: false },
  },
  grid: {
    show: false,
  },
  legend: {
    show: false,
  },
  dataLabels: {
    enabled: false,
  },
  fill: {
    opacity: 1,
  },
};

export const MONTH_NAMES = [
  '',
  'Ene',
  'Feb',
  'Mar',
  'Abr',
  'May',
  'Jun',
  'Jul',
  'Ago',
  'Sep',
  'Oct',
  'Nov',
  'Dic',
];

export const DEFAULT_DONUT_CHART_COUNT: DonutChartOptions = {
  series: [0, 0],
  chart: { type: 'donut', height: 450 },
  labels: DONUT_CHART_LABELS,
  colors: DONUT_CHART_COLORS,
  fill: { opacity: 1 },
  stroke: { width: 2, colors: ['#fff'] },
  plotOptions: {
    pie: {
      donut: {
        size: '60%',
        labels: {
          show: true,
          total: {
            show: true,
            label: 'Total',
            formatter: (w) => {
              const neg = w.globals.series[0] || 0;
              const pos = w.globals.series[1] || 0;
              return (neg + pos).toString();
            },
          },
        },
      },
    },
  },
  dataLabels: {
    enabled: true,
    formatter(val: number): string {
      return val.toFixed(0) + '%';
    },
  },
  legend: {
    position: 'bottom',
    formatter(legendName: string, opts: any): string {
      const value = opts.w.globals.series[opts.seriesIndex];
      return `${legendName}  = ${value}`;
    },
  },
  responsive: [
    {
      breakpoint: 480,
      options: { chart: { height: 250 }, legend: { position: 'bottom' } },
    },
  ],
};

export const COMPANY_VS_AGENT_LABELS = ['Gan. Empresa', 'Gan. Agente'];
export const COMPANY_VS_AGENT_COLORS = ['#7366FF', '#54BA4A'];

export const DEFAULT_COMPANY_VS_AGENT_DONUT: DonutChartOptions = {
  series: [0, 0],
  chart: { type: 'donut', height: 170 },
  labels: COMPANY_VS_AGENT_LABELS,
  colors: COMPANY_VS_AGENT_COLORS,
  fill: { opacity: 1 },
  stroke: { width: 2, colors: ['#fff'] },
  plotOptions: {
    pie: {
      donut: {
        size: '60%',
        labels: {
          show: true,
          total: {
            show: true,
            label: 'Neto',
            formatter: (w) => {
              const total =
                (w.globals.series[0] || 0) + (w.globals.series[1] || 0);
              if (total >= 1000000) return (total / 1000000).toFixed(1) + 'M';
              if (total >= 1000) return (total / 1000).toFixed(1) + 'K';
              return total.toFixed(0);
            },
          },
        },
      },
    },
  },
  dataLabels: {
    enabled: true,
    formatter(val: number): string {
      return val.toFixed(0) + '%';
    },
    style: { fontSize: '11px' },
    dropShadow: { enabled: false },
  },
  legend: {
    position: 'bottom',
    formatter(legendName: string, opts: any): string {
      const value = opts.w.globals.series[opts.seriesIndex];
      return `${legendName}  = ${value.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
    },
  },
  responsive: [
    {
      breakpoint: 480,
      options: { chart: { height: 250 }, legend: { position: 'bottom' } },
    },
  ],
};

export const DEFAULT_RADIAL_BAR_CHART: RadialBarChartOptions = {
  series: [0, 0],
  chart: {
    height: 200,
    type: 'radialBar',
  },
  plotOptions: {
    radialBar: {
      startAngle: 0,
      endAngle: 360,
      hollow: {
        margin: 4,
        size: '20%',
        background: 'transparent',
      },
      track: {
        background: 'rgba(255,255,255,0.08)',
        strokeWidth: '100%',
        margin: 4,
      },
      dataLabels: {
        name: {
          show: true,
          fontSize: '10px',
          offsetY: -4,
        },
        value: {
          show: true,
          fontSize: '12px',
          fontWeight: 600,
          offsetY: 4,
          formatter: (val: number) => val + '%',
        },
        total: {
          show: true,
          label: 'GGR',
          fontSize: '11px',
          formatter: (w: any) => w.globals.series[0] + '%',
        },
      },
    },
  },
  colors: ['#1ab7ea', '#0084ff'],
  labels: ['GGR', 'Apuesta'],
  legend: {
    show: false,
  },
  responsive: [
    {
      breakpoint: 480,
      options: { legend: { show: false } },
    },
  ],
};

export const DEFAULT_MODAL_CHART: ModalDetailChartOptions = {
  series: [],
  chart: {
    type: 'area',
    height: 350,
    toolbar: { show: true },
    zoom: { enabled: true },
  },
  colors: ['#7366FF'],
  stroke: {
    width: 3,
    curve: 'smooth',
  },
  xaxis: {
    categories: [],
    labels: {
      style: { fontSize: '12px' },
    },
  },
  yaxis: {
    labels: {
      formatter: (val: number) => {
        if (Math.abs(val) >= 1000000) return (val / 1000000).toFixed(1) + 'M';
        if (Math.abs(val) >= 1000) return (val / 1000).toFixed(1) + 'K';
        return val.toFixed(0);
      },
    },
  },
  grid: {
    borderColor: 'var(--chart-border)',
    strokeDashArray: 3,
  },
  legend: {
    show: true,
    position: 'top',
  },
  dataLabels: {
    enabled: true,
    formatter: (val: number) => {
      if (Math.abs(val) >= 1000000) return (val / 1000000).toFixed(1) + 'M';
      if (Math.abs(val) >= 1000) return (val / 1000).toFixed(1) + 'K';
      return val.toFixed(0);
    },
    style: { fontSize: '11px' },
  },
  fill: {
    type: 'gradient',
    gradient: {
      shadeIntensity: 1,
      opacityFrom: 0.4,
      opacityTo: 0.05,
    },
  },
  markers: {
    size: 5,
    hover: { sizeOffset: 3 },
  },
  tooltip: {
    y: {
      formatter: (val: number) =>
        '$' + val.toLocaleString('en-US', { minimumFractionDigits: 2 }),
    },
  },
};

export const PAGE_BALANCE_FORM_ALERTS = {
  PREVIOUS_BALANCE_UPDATED: {
    icon: 'warning',
    title: 'Balance anterior actualizado',
    html: `Los valores del mes anterior fueron modificados después de registrar este balance.<br><br>
      Los campos <strong>Balance Anterior</strong>, <strong>GGR Balance Anterior</strong> y <strong>Apuesta Anterior</strong>
      fueron actualizados con los valores correctos. Guarda para cuadrar valores.`,
    confirmButtonText: 'Entendido',
    confirmButtonColor: '#f59e0b',
  },
  REQUIRED_FIELDS: {
    icon: 'warning',
    title: 'Campos requeridos',
    text: 'Por favor seleccione un agente y una fecha.',
  },
  UPDATE_SUCCESS: {
    icon: 'success',
    title: 'Balance actualizado',
    timer: 1500,
    showConfirmButton: false,
  },
  UPDATE_ERROR: {
    icon: 'error',
    title: 'Error al actualizar',
    text: 'Por favor, intente nuevamente.',
  },
  CREATE_SUCCESS: {
    icon: 'success',
    title: 'Balance creado',
    timer: 1500,
    showConfirmButton: false,
  },
  CREATE_CONFLICT: {
    icon: 'warning',
    title: 'Balance ya existe',
    text: 'Por favor, intente nuevamente.',
  },
  CREATE_ERROR: {
    icon: 'error',
    title: 'Error al crear',
    text: 'Por favor, intente nuevamente.',
  },
};

export const NUMERIC_PARAM_FIELDS = [
  'balance',
  'result',
  'actualBalance',
  'lastBalance',
  'ggrCasinoBalance',
  'ggrResult',
  'betResult',
  'companyProfit',
  'totalReferralProfit',
  'netProfitPercent',
];

export const USER_SESSION = 'USER_SESSION';
export const USER_SESSION_PRE = 'USER_SESSION_PRE';

export const INITIAL_BALANCE_LOAD_STATE: BalanceLoadState = {
  isOpen: false,
  isMinimized: false,
  isConnecting: false,
  isLoading: false,
  isDone: false,
  hasError: false,
  errorMsg: '',
  month: '',
  totalAgents: 0,
  page: 0,
  totalInserted: 0,
  totalSkipped: 0,
  totalNotFound: 0,
  insertedAgents: [],
  skippedAgents: [],
  notFoundAgents: [],
  showInserted: true,
  showSkipped: true,
  showNotFound: true,
};
